import { TFunction } from "i18next";

export interface OptionItem {
  key: string;
  text: string;
}

// Insurance products options (from original: rbMtpl, rbCasco, rbMtplCasco)
export const getInsuranceProductOptions = (t: TFunction): OptionItem[] => [
  { key: "MTPL", text: t("qtg.proposal.proposalData.contents.mtpl") },
  { key: "CASCO", text: t("qtg.proposal.proposalData.contents.casco") },
];

// Sale channel options (from dropSaleChannels - exact values from screenshot)
export const saleChannelOptions: OptionItem[] = [
  { key: "corporate", text: "Corporate" },
  { key: "lizi_lv", text: "lizi.lv" },
  { key: "corporate_2", text: "Corporate" },
  { key: "salidzini_lv", text: "salidzini.lv" },
  { key: "brokers_bulk", text: "Brokers (bulk)" },
  { key: "gudriem_lv", text: "gudriem.lv" },
  { key: "dauto", text: "Dauto" },
  { key: "kurpirkt_lv", text: "kurpirkt.lv" },
  { key: "facebook", text: "Facebook" },
  { key: "innoweb", text: "InnoWeb" },
  { key: "mvkn", text: "MVKn" },
  { key: "4_finance", text: "4 Finance" },
  { key: "mobilly", text: "Mobilly" },
  { key: "vfs", text: "VFS" },
  { key: "ezauto", text: "EZauto" },
  { key: "musa_motors", text: "Musa Motors" },
  { key: "vmobile", text: "Vmobile" },
  { key: "nordea_brokers", text: "Nordea Brokers" },
  { key: "mai_lv", text: "MAI LV" },
  { key: "mai_eu", text: "MAI EU" },
];

// Proposal type options (from dropProposalType - actual values from screenshot)
export const proposalTypeOptions: OptionItem[] = [
  { key: "new", text: "New" },
  { key: "order", text: "Order" },
  { key: "renewal", text: "Renewal" },
  { key: "third_party", text: "Third Party" },
];

// Status options (from dropMtplProposalStatus/dropCascoProposalStatus - ProposalStatuses enum)
export const statusOptions: OptionItem[] = [
  { key: "-1", text: "-" },
  { key: "1", text: "In Progress" }, // ProposalStatuses.InProgress
  { key: "2", text: "Registered" }, // ProposalStatuses.Registered
  { key: "3", text: "Sent" }, // ProposalStatuses.Sent
  { key: "4", text: "Ready" }, // ProposalStatuses.Ready
  { key: "5", text: "Accepted" }, // ProposalStatuses.Accepted
  { key: "6", text: "Declined" }, // ProposalStatuses.Declined
  { key: "7", text: "Rejected" }, // ProposalStatuses.Rejected
  { key: "8", text: "Issued" }, // ProposalStatuses.Issued
];

// Rejection reason options (from dropMtplRejectionReason/dropCascoRejectionReason - actual values from screenshot)
export const rejectionReasonOptions: OptionItem[] = [
  { key: "-", text: "-" },
  { key: "expired", text: "Expired" },
  {
    key: "other_broker_offered_better_service",
    text: "Other broker offered better service",
  },
  {
    key: "insurer_direct_offer_was_better",
    text: "Insurer direct offer was better",
  },
  { key: "car_is_not_in_use", text: "Car is not in use" },
  {
    key: "did_not_want_to_insure_at_all",
    text: "Did not want to insure at all",
  },
  { key: "active_policy_in_ltab", text: "Active policy in LTAB" },
];

// Currency options
export const currencyOptions: OptionItem[] = [
  { key: "EUR", text: "EUR" },
  { key: "USD", text: "USD" },
  { key: "GBP", text: "GBP" },
];

// Object group options
export const objectGroupOptions: OptionItem[] = [
  { key: "M1", text: "M1 - Passenger Cars" },
  { key: "M2", text: "M2 - Large Passenger Vehicles" },
  { key: "M3", text: "M3 - Buses" },
  { key: "N1", text: "N1 - Light Commercial" },
  { key: "N2", text: "N2 - Medium Commercial" },
  { key: "N3", text: "N3 - Heavy Commercial" },
];

// Body type options
export const bodyTypeOptions: OptionItem[] = [
  { key: "sedan", text: "Sedan" },
  { key: "hatchback", text: "Hatchback" },
  { key: "suv", text: "SUV" },
  { key: "coupe", text: "Coupe" },
  { key: "wagon", text: "Wagon" },
  { key: "convertible", text: "Convertible" },
  { key: "minivan", text: "Minivan" },
  { key: "pickup", text: "Pickup" },
];

// Fuel type options
export const fuelTypeOptions: OptionItem[] = [
  { key: "petrol", text: "Petrol" },
  { key: "diesel", text: "Diesel" },
  { key: "electric", text: "Electric" },
  { key: "hybrid", text: "Hybrid" },
  { key: "lpg", text: "LPG" },
  { key: "cng", text: "CNG" },
];

// Special use options
export const specialUseOptions: OptionItem[] = [
  { key: "none", text: "None" },
  { key: "taxi", text: "Taxi" },
  { key: "driving_school", text: "Driving School" },
  { key: "emergency", text: "Emergency" },
  { key: "rental", text: "Rental" },
  { key: "delivery", text: "Delivery" },
];

// Abroad use options
export const abroadUseOptions: OptionItem[] = [
  { key: "no", text: "No" },
  { key: "eu", text: "EU" },
  { key: "worldwide", text: "Worldwide" },
];

// Leasing company options
export const leasingCompanyOptions: OptionItem[] = [
  { key: "none", text: "None" },
  { key: "swedbank", text: "Swedbank" },
  { key: "seb", text: "SEB" },
  { key: "luminor", text: "Luminor" },
  { key: "other", text: "Other" },
];

// Glass insurance options
export const glassInsuranceOptions: OptionItem[] = [
  { key: "none", text: "None" },
  { key: "basic", text: "Basic" },
  { key: "full", text: "Full" },
];

// Deductible options
export const deductibleOptions: OptionItem[] = [
  { key: "0", text: "0 EUR" },
  { key: "150", text: "150 EUR" },
  { key: "300", text: "300 EUR" },
  { key: "500", text: "500 EUR" },
  { key: "1000", text: "1000 EUR" },
];

// Repair location options (for CASCO)
export const getRepairLocationOptions = (t: TFunction): OptionItem[] => [
  {
    key: "authorized_service",
    text: t("qtg.proposal.vehicleData.options.authorizedService"),
  },
  {
    key: "any_service",
    text: t("qtg.proposal.vehicleData.options.anyService"),
  },
  { key: "garage", text: t("qtg.proposal.vehicleData.options.garage") },
  { key: "own_repair", text: t("qtg.proposal.vehicleData.options.ownRepair") },
];

// Client type options (Person/Company)
export const getClientTypeOptions = (t: TFunction): OptionItem[] => [
  { key: "person", text: t("qtg.proposal.clientData.options.person") },
  { key: "company", text: t("qtg.proposal.clientData.options.company") },
];

// Region options
export const getRegionOptions = (t: TFunction): OptionItem[] => [
  { key: "-1", text: "-" },
  {
    key: "0005000",
    text: "Liepāja",
  },
  {
    key: "0007000",
    text: "Ventspils",
  },
  {
    key: "0027000",
    text: "Dienvidkurzemes novads",
  },
  {
    key: "0033000",
    text: "Kuldīgas novads",
  },
  {
    key: "0046000",
    text: "Saldus novads",
  },
  {
    key: "0051000",
    text: "Talsu novads",
  },
  {
    key: "0056000",
    text: "Ventspils novads",
  },
  {
    key: "0002000",
    text: "Daugavpils",
  },
  {
    key: "0006000",
    text: "Rēzekne",
  },
  {
    key: "0022000",
    text: "Augšdaugavas novads",
  },
  {
    key: "0024000",
    text: "Balvu novads",
  },
  {
    key: "0032000",
    text: "Krāslavas novads",
  },
  {
    key: "0036000",
    text: "Līvānu novads",
  },
  {
    key: "0037000",
    text: "Ludzas novads",
  },
  {
    key: "0042000",
    text: "Preiļu novads",
  },
  {
    key: "0043000",
    text: "Rēzeknes novads",
  },
  {
    key: "0001000",
    text: "Rīga",
  },
  {
    key: "0004000",
    text: "Jūrmala",
  },
  {
    key: "0023000",
    text: "Ādažu novads",
  },
  {
    key: "0034000",
    text: "Ķekavas novads",
  },
  {
    key: "0035000",
    text: "Limbažu novads",
  },
  {
    key: "0039000",
    text: "Mārupes novads",
  },
  {
    key: "0040000",
    text: "Ogres novads",
  },
  {
    key: "0041000",
    text: "Olaines novads",
  },
  {
    key: "0044000",
    text: "Ropažu novads",
  },
  {
    key: "0045000",
    text: "Salaspils novads",
  },
  {
    key: "0047000",
    text: "Saulkrastu novads",
  },
  {
    key: "0048000",
    text: "Siguldas novads",
  },
  {
    key: "0052000",
    text: "Tukuma novads",
  },
  {
    key: "0021000",
    text: "Alūksnes novads",
  },
  {
    key: "0026000",
    text: "Cēsu novads",
  },
  {
    key: "0029000",
    text: "Gulbenes novads",
  },
  {
    key: "0038001",
    text: "Madonas novads",
  },
  {
    key: "0049000",
    text: "Smiltenes novads",
  },
  {
    key: "0053000",
    text: "Valkas novads",
  },
  {
    key: "0054000",
    text: "Valmieras novads",
  },
  {
    key: "0003000",
    text: "Jelgava",
  },
  {
    key: "0020000",
    text: "Aizkraukles novads",
  },
  {
    key: "0025000",
    text: "Bauskas novads",
  },
  {
    key: "0028000",
    text: "Dobeles novads",
  },
  {
    key: "0030000",
    text: "Jelgavas novads",
  },
  {
    key: "0031000",
    text: "Jēkabpils novads",
  },
  {
    key: "0027200",
    text: "Aizpute",
  },
  {
    key: "0027210",
    text: "Durbe",
  },
  {
    key: "0027220",
    text: "Grobiņa",
  },
  {
    key: "0027230",
    text: "Pāvilosta",
  },
  {
    key: "0027240",
    text: "Priekule",
  },
  {
    key: "0027400",
    text: "Aizputes pagasts",
  },
  {
    key: "0027410",
    text: "Bārtas pagasts",
  },
  {
    key: "0027420",
    text: "Bunkas pagasts",
  },
  {
    key: "0027430",
    text: "Cīravas pagasts",
  },
  {
    key: "0027440",
    text: "Dunalkas pagasts",
  },
  {
    key: "0027450",
    text: "Dunikas pagasts",
  },
  {
    key: "0027460",
    text: "Durbes pagasts",
  },
  {
    key: "0027470",
    text: "Embūtes pagasts",
  },
  {
    key: "0027480",
    text: "Gaviezes pagasts",
  },
  {
    key: "0027490",
    text: "Gramzdas pagasts",
  },
  {
    key: "0027500",
    text: "Grobiņas pagasts",
  },
  {
    key: "0027510",
    text: "Kalētu pagasts",
  },
  {
    key: "0027520",
    text: "Kalvenes pagasts",
  },
  {
    key: "0027530",
    text: "Kazdangas pagasts",
  },
  {
    key: "0027540",
    text: "Lažas pagasts",
  },
  {
    key: "0027550",
    text: "Medzes pagasts",
  },
  {
    key: "0027560",
    text: "Nīcas pagasts",
  },
  {
    key: "0027570",
    text: "Otaņķu pagasts",
  },
  {
    key: "0027580",
    text: "Priekules pagasts",
  },
  {
    key: "0027590",
    text: "Rucavas pagasts",
  },
  {
    key: "0027600",
    text: "Sakas pagasts",
  },
  {
    key: "0027610",
    text: "Tadaiķu pagasts",
  },
  {
    key: "0027620",
    text: "Vaiņodes pagasts",
  },
  {
    key: "0027630",
    text: "Vecpils pagasts",
  },
  {
    key: "0027640",
    text: "Vērgales pagasts",
  },
  {
    key: "0027650",
    text: "Virgas pagasts",
  },
  {
    key: "0033200",
    text: "Kuldīga",
  },
  {
    key: "0033210",
    text: "Skrunda",
  },
  {
    key: "0033400",
    text: "Alsungas pagasts",
  },
  {
    key: "0033410",
    text: "Ēdoles pagasts",
  },
  {
    key: "0033420",
    text: "Gudenieku pagasts",
  },
  {
    key: "0033430",
    text: "Īvandes pagasts",
  },
  {
    key: "0033440",
    text: "Kabiles pagasts",
  },
  {
    key: "0033450",
    text: "Kurmāles pagasts",
  },
  {
    key: "0033460",
    text: "Laidu pagasts",
  },
  {
    key: "0033470",
    text: "Nīkrāces pagasts",
  },
  {
    key: "0033480",
    text: "Padures pagasts",
  },
  {
    key: "0033490",
    text: "Pelču pagasts",
  },
  {
    key: "0033500",
    text: "Raņķu pagasts",
  },
  {
    key: "0033510",
    text: "Rendas pagasts",
  },
  {
    key: "0033520",
    text: "Rudbāržu pagasts",
  },
  {
    key: "0033530",
    text: "Rumbas pagasts",
  },
  {
    key: "0033540",
    text: "Skrundas pagasts",
  },
  {
    key: "0033550",
    text: "Snēpeles pagasts",
  },
  {
    key: "0033560",
    text: "Turlavas pagasts",
  },
  {
    key: "0033570",
    text: "Vārmes pagasts",
  },
  {
    key: "0046200",
    text: "Brocēni",
  },
  {
    key: "0046210",
    text: "Saldus",
  },
  {
    key: "0046400",
    text: "Blīdenes pagasts",
  },
  {
    key: "0046410",
    text: "Cieceres pagasts",
  },
  {
    key: "0046420",
    text: "Ezeres pagasts",
  },
  {
    key: "0046430",
    text: "Gaiķu pagasts",
  },
  {
    key: "0046440",
    text: "Jaunauces pagasts",
  },
  {
    key: "0046450",
    text: "Jaunlutriņu pagasts",
  },
  {
    key: "0046460",
    text: "Kursīšu pagasts",
  },
  {
    key: "0046470",
    text: "Lutriņu pagasts",
  },
  {
    key: "0046480",
    text: "Nīgrandes pagasts",
  },
  {
    key: "0046490",
    text: "Novadnieku pagasts",
  },
  {
    key: "0046500",
    text: "Pampāļu pagasts",
  },
  {
    key: "0046510",
    text: "Remtes pagasts",
  },
  {
    key: "0046520",
    text: "Rubas pagasts",
  },
  {
    key: "0046530",
    text: "Saldus pagasts",
  },
  {
    key: "0046540",
    text: "Šķēdes pagasts",
  },
  {
    key: "0046550",
    text: "Vadakstes pagasts",
  },
  {
    key: "0046560",
    text: "Zaņas pagasts",
  },
  {
    key: "0046570",
    text: "Zirņu pagasts",
  },
  {
    key: "0046580",
    text: "Zvārdes pagasts",
  },
  {
    key: "0051200",
    text: "Sabile",
  },
  {
    key: "0051210",
    text: "Stende",
  },
  {
    key: "0051220",
    text: "Talsi",
  },
  {
    key: "0051230",
    text: "Valdemārpils",
  },
  {
    key: "0051400",
    text: "Abavas pagasts",
  },
  {
    key: "0051410",
    text: "Ārlavas pagasts",
  },
  {
    key: "0051420",
    text: "Balgales pagasts",
  },
  {
    key: "0051430",
    text: "Dundagas pagasts",
  },
  {
    key: "0051440",
    text: "Ģibuļu pagasts",
  },
  {
    key: "0051450",
    text: "Īves pagasts",
  },
  {
    key: "0051460",
    text: "Kolkas pagasts",
  },
  {
    key: "0051470",
    text: "Ķūļciema pagasts",
  },
  {
    key: "0051480",
    text: "Laidzes pagasts",
  },
  {
    key: "0051490",
    text: "Laucienes pagasts",
  },
  {
    key: "0051500",
    text: "Lībagu pagasts",
  },
  {
    key: "0051510",
    text: "Lubes pagasts",
  },
  {
    key: "0051520",
    text: "Mērsraga pagasts",
  },
  {
    key: "0051530",
    text: "Rojas pagasts",
  },
  {
    key: "0051540",
    text: "Strazdes pagasts",
  },
  {
    key: "0051550",
    text: "Valdgales pagasts",
  },
  {
    key: "0051560",
    text: "Vandzenes pagasts",
  },
  {
    key: "0051570",
    text: "Virbu pagasts",
  },
  {
    key: "0056200",
    text: "Piltene",
  },
  {
    key: "0056400",
    text: "Ances pagasts",
  },
  {
    key: "0056410",
    text: "Jūrkalnes pagasts",
  },
  {
    key: "0056420",
    text: "Piltenes pagasts",
  },
  {
    key: "0056430",
    text: "Popes pagasts",
  },
  {
    key: "0056440",
    text: "Puzes pagasts",
  },
  {
    key: "0056450",
    text: "Tārgales pagasts",
  },
  {
    key: "0056460",
    text: "Ugāles pagasts",
  },
  {
    key: "0056470",
    text: "Usmas pagasts",
  },
  {
    key: "0056480",
    text: "Užavas pagasts",
  },
  {
    key: "0056490",
    text: "Vārves pagasts",
  },
  {
    key: "0056500",
    text: "Ziru pagasts",
  },
  {
    key: "0056510",
    text: "Zlēku pagasts",
  },
  {
    key: "",
    text: "",
  },
  {
    key: "0022200",
    text: "Ilūkste",
  },
  {
    key: "0022210",
    text: "Subate",
  },
  {
    key: "0022400",
    text: "Ambeļu pagasts",
  },
  {
    key: "0022410",
    text: "Bebrenes pagasts",
  },
  {
    key: "0022420",
    text: "Biķernieku pagasts",
  },
  {
    key: "0022430",
    text: "Demenes pagasts",
  },
  {
    key: "0022440",
    text: "Dubnas pagasts",
  },
  {
    key: "0022450",
    text: "Dvietes pagasts",
  },
  {
    key: "0022460",
    text: "Eglaines pagasts",
  },
  {
    key: "0022470",
    text: "Kalkūnes pagasts",
  },
  {
    key: "0022480",
    text: "Kalupes pagasts",
  },
  {
    key: "0022490",
    text: "Laucesas pagasts",
  },
  {
    key: "0022500",
    text: "Līksnas pagasts",
  },
  {
    key: "0022510",
    text: "Maļinovas pagasts",
  },
  {
    key: "0022520",
    text: "Medumu pagasts",
  },
  {
    key: "0022530",
    text: "Naujenes pagasts",
  },
  {
    key: "0022540",
    text: "Nīcgales pagasts",
  },
  {
    key: "0022550",
    text: "Pilskalnes pagasts",
  },
  {
    key: "0022560",
    text: "Prodes pagasts",
  },
  {
    key: "0022570",
    text: "Salienas pagasts",
  },
  {
    key: "0022580",
    text: "Skrudalienas pagasts",
  },
  {
    key: "0022590",
    text: "Sventes pagasts",
  },
  {
    key: "0022600",
    text: "Šēderes pagasts",
  },
  {
    key: "0022610",
    text: "Tabores pagasts",
  },
  {
    key: "0022620",
    text: "Vaboles pagasts",
  },
  {
    key: "0022630",
    text: "Vecsalienas pagasts",
  },
  {
    key: "0022640",
    text: "Višķu pagasts",
  },
  {
    key: "0024200",
    text: "Balvi",
  },
  {
    key: "0024210",
    text: "Viļaka",
  },
  {
    key: "0024400",
    text: "Baltinavas pagasts",
  },
  {
    key: "0024410",
    text: "Balvu pagasts",
  },
  {
    key: "0024420",
    text: "Bērzkalnes pagasts",
  },
  {
    key: "0024430",
    text: "Bērzpils pagasts",
  },
  {
    key: "0024440",
    text: "Briežuciema pagasts",
  },
  {
    key: "0024450",
    text: "Krišjāņu pagasts",
  },
  {
    key: "0024460",
    text: "Kubulu pagasts",
  },
  {
    key: "0024470",
    text: "Kupravas pagasts",
  },
  {
    key: "0024480",
    text: "Lazdukalna pagasts",
  },
  {
    key: "0024490",
    text: "Lazdulejas pagasts",
  },
  {
    key: "0024500",
    text: "Medņevas pagasts",
  },
  {
    key: "0024510",
    text: "Rugāju pagasts",
  },
  {
    key: "0024520",
    text: "Susāju pagasts",
  },
  {
    key: "0024530",
    text: "Šķilbēnu pagasts",
  },
  {
    key: "0024540",
    text: "Tilžas pagasts",
  },
  {
    key: "0024550",
    text: "Vectilžas pagasts",
  },
  {
    key: "0024560",
    text: "Vecumu pagasts",
  },
  {
    key: "0024570",
    text: "Vīksnas pagasts",
  },
  {
    key: "0024580",
    text: "Žīguru pagasts",
  },
  {
    key: "0032200",
    text: "Dagda",
  },
  {
    key: "0032210",
    text: "Krāslava",
  },
  {
    key: "0032400",
    text: "Andrupenes pagasts",
  },
  {
    key: "0032410",
    text: "Andzeļu pagasts",
  },
  {
    key: "0032420",
    text: "Asūnes pagasts",
  },
  {
    key: "0032430",
    text: "Aulejas pagasts",
  },
  {
    key: "0032440",
    text: "Bērziņu pagasts",
  },
  {
    key: "0032450",
    text: "Dagdas pagasts",
  },
  {
    key: "0032460",
    text: "Ezernieku pagasts",
  },
  {
    key: "0032470",
    text: "Grāveru pagasts",
  },
  {
    key: "0032480",
    text: "Indras pagasts",
  },
  {
    key: "0032490",
    text: "Izvaltas pagasts",
  },
  {
    key: "0032500",
    text: "Kalniešu pagasts",
  },
  {
    key: "0032510",
    text: "Kaplavas pagasts",
  },
  {
    key: "0032520",
    text: "Kastuļinas pagasts",
  },
  {
    key: "0032530",
    text: "Kombuļu pagasts",
  },
  {
    key: "0032540",
    text: "Konstantinovas pagasts",
  },
  {
    key: "0032550",
    text: "Krāslavas pagasts",
  },
  {
    key: "0032560",
    text: "Ķepovas pagasts",
  },
  {
    key: "0032570",
    text: "Piedrujas pagasts",
  },
  {
    key: "0032580",
    text: "Robežnieku pagasts",
  },
  {
    key: "0032590",
    text: "Skaistas pagasts",
  },
  {
    key: "0032600",
    text: "Svariņu pagasts",
  },
  {
    key: "0032610",
    text: "Šķaunes pagasts",
  },
  {
    key: "0032620",
    text: "Šķeltovas pagasts",
  },
  {
    key: "0032630",
    text: "Ūdrīšu pagasts",
  },
  {
    key: "0036200",
    text: "Līvāni",
  },
  {
    key: "0036400",
    text: "Jersikas pagasts",
  },
  {
    key: "0036410",
    text: "Rožupes pagasts",
  },
  {
    key: "0036420",
    text: "Rudzātu pagasts",
  },
  {
    key: "0036430",
    text: "Sutru pagasts",
  },
  {
    key: "0036440",
    text: "Turku pagasts",
  },
  {
    key: "0037200",
    text: "Kārsava",
  },
  {
    key: "0037210",
    text: "Ludza",
  },
  {
    key: "0037220",
    text: "Zilupe",
  },
  {
    key: "0037400",
    text: "Blontu pagasts",
  },
  {
    key: "0037410",
    text: "Briģu pagasts",
  },
  {
    key: "0037420",
    text: "Ciblas pagasts",
  },
  {
    key: "0037430",
    text: "Cirmas pagasts",
  },
  {
    key: "0037440",
    text: "Goliševas pagasts",
  },
  {
    key: "0037450",
    text: "Isnaudas pagasts",
  },
  {
    key: "0037460",
    text: "Istras pagasts",
  },
  {
    key: "0037470",
    text: "Lauderu pagasts",
  },
  {
    key: "0037480",
    text: "Līdumnieku pagasts",
  },
  {
    key: "0037490",
    text: "Malnavas pagasts",
  },
  {
    key: "0037500",
    text: "Mežvidu pagasts",
  },
  {
    key: "0037510",
    text: "Mērdzenes pagasts",
  },
  {
    key: "0037520",
    text: "Nirzas pagasts",
  },
  {
    key: "0037530",
    text: "Ņukšu pagasts",
  },
  {
    key: "0037540",
    text: "Pasienes pagasts",
  },
  {
    key: "0037550",
    text: "Pildas pagasts",
  },
  {
    key: "0037560",
    text: "Pureņu pagasts",
  },
  {
    key: "0037570",
    text: "Pušmucovas pagasts",
  },
  {
    key: "0037580",
    text: "Rundēnu pagasts",
  },
  {
    key: "0037590",
    text: "Salnavas pagasts",
  },
  {
    key: "0037600",
    text: "Zaļesjes pagasts",
  },
  {
    key: "0037610",
    text: "Zvirgzdenes pagasts",
  },
  {
    key: "0042200",
    text: "Preiļi",
  },
  {
    key: "0042400",
    text: "Aglonas pagasts",
  },
  {
    key: "0042410",
    text: "Aizkalnes pagasts",
  },
  {
    key: "0042420",
    text: "Galēnu pagasts",
  },
  {
    key: "0042430",
    text: "Pelēču pagasts",
  },
  {
    key: "0042440",
    text: "Preiļu pagasts",
  },
  {
    key: "0042450",
    text: "Riebiņu pagasts",
  },
  {
    key: "0042460",
    text: "Rožkalnu pagasts",
  },
  {
    key: "0042470",
    text: "Rušonas pagasts",
  },
  {
    key: "0042480",
    text: "Saunas pagasts",
  },
  {
    key: "0042490",
    text: "Silajāņu pagasts",
  },
  {
    key: "0042500",
    text: "Sīļukalna pagasts",
  },
  {
    key: "0042510",
    text: "Stabulnieku pagasts",
  },
  {
    key: "0042520",
    text: "Upmalas pagasts",
  },
  {
    key: "0042530",
    text: "Vārkavas pagasts",
  },
  {
    key: "0043200",
    text: "Viļāni",
  },
  {
    key: "0043400",
    text: "Audriņu pagasts",
  },
  {
    key: "0043410",
    text: "Bērzgales pagasts",
  },
  {
    key: "0043420",
    text: "Čornajas pagasts",
  },
  {
    key: "0043430",
    text: "Dekšāres pagasts",
  },
  {
    key: "0043440",
    text: "Dricānu pagasts",
  },
  {
    key: "0043450",
    text: "Feimaņu pagasts",
  },
  {
    key: "0043460",
    text: "Gaigalavas pagasts",
  },
  {
    key: "0043470",
    text: "Griškānu pagasts",
  },
  {
    key: "0043480",
    text: "Ilzeskalna pagasts",
  },
  {
    key: "0043490",
    text: "Kantinieku pagasts",
  },
  {
    key: "0043500",
    text: "Kaunatas pagasts",
  },
  {
    key: "0043510",
    text: "Lendžu pagasts",
  },
  {
    key: "0043520",
    text: "Lūznavas pagasts",
  },
  {
    key: "0043530",
    text: "Maltas pagasts",
  },
  {
    key: "0043540",
    text: "Mākoņkalna pagasts",
  },
  {
    key: "0043550",
    text: "Nagļu pagasts",
  },
  {
    key: "0043560",
    text: "Nautrēnu pagasts",
  },
  {
    key: "0043570",
    text: "Ozolaines pagasts",
  },
  {
    key: "0043580",
    text: "Ozolmuižas pagasts",
  },
  {
    key: "0043590",
    text: "Pušas pagasts",
  },
  {
    key: "0043600",
    text: "Rikavas pagasts",
  },
  {
    key: "0043610",
    text: "Sakstagala pagasts",
  },
  {
    key: "0043620",
    text: "Silmalas pagasts",
  },
  {
    key: "0043630",
    text: "Sokolku pagasts",
  },
  {
    key: "0043640",
    text: "Stoļerovas pagasts",
  },
  {
    key: "0043650",
    text: "Stružānu pagasts",
  },
  {
    key: "0043660",
    text: "Vērēmu pagasts",
  },
  {
    key: "0043670",
    text: "Viļānu pagasts",
  },
  {
    key: "0023200",
    text: "Ādaži",
  },
  {
    key: "0023401",
    text: "Ādažu pagasts",
  },
  {
    key: "0023410",
    text: "Carnikavas pagasts",
  },
  {
    key: "0034200",
    text: "Baldone",
  },
  {
    key: "0034210",
    text: "Baloži",
  },
  {
    key: "0034220",
    text: "Ķekava",
  },
  {
    key: "0034400",
    text: "Baldones pagasts",
  },
  {
    key: "0034410",
    text: "Daugmales pagasts",
  },
  {
    key: "0034421",
    text: "Ķekavas pagasts",
  },
  {
    key: "0035200",
    text: "Ainaži",
  },
  {
    key: "0035210",
    text: "Aloja",
  },
  {
    key: "0035220",
    text: "Limbaži",
  },
  {
    key: "0035230",
    text: "Salacgrīva",
  },
  {
    key: "0035240",
    text: "Staicele",
  },
  {
    key: "0035400",
    text: "Ainažu pagasts",
  },
  {
    key: "0035410",
    text: "Alojas pagasts",
  },
  {
    key: "0035420",
    text: "Braslavas pagasts",
  },
  {
    key: "0035430",
    text: "Brīvzemnieku pagasts",
  },
  {
    key: "0035440",
    text: "Katvaru pagasts",
  },
  {
    key: "0035450",
    text: "Liepupes pagasts",
  },
  {
    key: "0035460",
    text: "Limbažu pagasts",
  },
  {
    key: "0035470",
    text: "Pāles pagasts",
  },
  {
    key: "0035480",
    text: "Salacgrīvas pagasts",
  },
  {
    key: "0035490",
    text: "Skultes pagasts",
  },
  {
    key: "0035500",
    text: "Staiceles pagasts",
  },
  {
    key: "0035510",
    text: "Umurgas pagasts",
  },
  {
    key: "0035520",
    text: "Vidrižu pagasts",
  },
  {
    key: "0035530",
    text: "Viļķenes pagasts",
  },
  {
    key: "0039200",
    text: "Mārupe",
  },
  {
    key: "0039400",
    text: "Babītes pagasts",
  },
  {
    key: "0039411",
    text: "Mārupes pagasts",
  },
  {
    key: "0039420",
    text: "Salas pagasts",
  },
  {
    key: "0040010",
    text: "Ogre",
  },
  {
    key: "0040200",
    text: "Ikšķile",
  },
  {
    key: "0040210",
    text: "Ķegums",
  },
  {
    key: "0040220",
    text: "Lielvārde",
  },
  {
    key: "0040400",
    text: "Birzgales pagasts",
  },
  {
    key: "0040410",
    text: "Jumpravas pagasts",
  },
  {
    key: "0040420",
    text: "Krapes pagasts",
  },
  {
    key: "0040430",
    text: "Ķeipenes pagasts",
  },
  {
    key: "0040440",
    text: "Lauberes pagasts",
  },
  {
    key: "0040450",
    text: "Lēdmanes pagasts",
  },
  {
    key: "0040460",
    text: "Lielvārdes pagasts",
  },
  {
    key: "0040470",
    text: "Madlienas pagasts",
  },
  {
    key: "0040480",
    text: "Mazozolu pagasts",
  },
  {
    key: "0040490",
    text: "Meņģeles pagasts",
  },
  {
    key: "0040500",
    text: "Ogresgala pagasts",
  },
  {
    key: "0040510",
    text: "Rembates pagasts",
  },
  {
    key: "0040520",
    text: "Suntažu pagasts",
  },
  {
    key: "0040530",
    text: "Taurupes pagasts",
  },
  {
    key: "0040540",
    text: "Tīnūžu pagasts",
  },
  {
    key: "0040550",
    text: "Tomes pagasts",
  },
  {
    key: "0041200",
    text: "Olaine",
  },
  {
    key: "0041400",
    text: "Olaines pagasts",
  },
  {
    key: "0044200",
    text: "Vangaži",
  },
  {
    key: "0044400",
    text: "Garkalnes pagasts",
  },
  {
    key: "0044410",
    text: "Ropažu pagasts",
  },
  {
    key: "0044420",
    text: "Stopiņu pagasts",
  },
  {
    key: "0045200",
    text: "Salaspils ",
  },
  {
    key: "0045400",
    text: "Salaspils pagasts",
  },
  {
    key: "0047200",
    text: "Saulkrasti",
  },
  {
    key: "0047400",
    text: "Saulkrastu pagasts",
  },
  {
    key: "0047410",
    text: "Sējas pagasts",
  },
  {
    key: "0048200",
    text: "Sigulda",
  },
  {
    key: "0048400",
    text: "Allažu pagasts",
  },
  {
    key: "0048410",
    text: "Inčukalna pagasts",
  },
  {
    key: "0048420",
    text: "Krimuldas pagasts",
  },
  {
    key: "0048430",
    text: "Lēdurgas pagasts",
  },
  {
    key: "0048440",
    text: "Mālpils pagasts",
  },
  {
    key: "0048450",
    text: "Mores pagasts",
  },
  {
    key: "0048460",
    text: "Siguldas pagasts",
  },
  {
    key: "0052200",
    text: "Kandava",
  },
  {
    key: "0052210",
    text: "Tukums",
  },
  {
    key: "0052400",
    text: "Cēres pagasts",
  },
  {
    key: "0052410",
    text: "Degoles pagasts",
  },
  {
    key: "0052420",
    text: "Džūkstes pagasts",
  },
  {
    key: "0052430",
    text: "Engures pagasts",
  },
  {
    key: "0052440",
    text: "Irlavas pagasts",
  },
  {
    key: "0052450",
    text: "Jaunpils pagasts",
  },
  {
    key: "0052460",
    text: "Jaunsātu pagasts",
  },
  {
    key: "0052470",
    text: "Kandavas pagasts",
  },
  {
    key: "0052480",
    text: "Lapmežciema pagasts",
  },
  {
    key: "0052490",
    text: "Lestenes pagasts",
  },
  {
    key: "0052500",
    text: "Matkules pagasts",
  },
  {
    key: "0052510",
    text: "Pūres pagasts",
  },
  {
    key: "0052520",
    text: "Sēmes pagasts",
  },
  {
    key: "0052530",
    text: "Slampes pagasts",
  },
  {
    key: "0052540",
    text: "Smārdes pagasts",
  },
  {
    key: "0052550",
    text: "Tumes pagasts",
  },
  {
    key: "0052560",
    text: "Vānes pagasts",
  },
  {
    key: "0052570",
    text: "Viesatu pagasts",
  },
  {
    key: "0052580",
    text: "Zantes pagasts",
  },
  {
    key: "0052590",
    text: "Zemītes pagasts",
  },
  {
    key: "0052600",
    text: "Zentenes pagasts",
  },
  {
    key: "0021200",
    text: "Alūksne",
  },
  {
    key: "0021400",
    text: "Alsviķu pagasts",
  },
  {
    key: "0021410",
    text: "Annas pagasts",
  },
  {
    key: "0021420",
    text: "Ilzenes pagasts",
  },
  {
    key: "0021430",
    text: "Jaunalūksnes pagasts",
  },
  {
    key: "0021440",
    text: "Jaunannas pagasts",
  },
  {
    key: "0021450",
    text: "Jaunlaicenes pagasts",
  },
  {
    key: "0021460",
    text: "Kalncempju pagasts",
  },
  {
    key: "0021470",
    text: "Liepnas pagasts",
  },
  {
    key: "0021480",
    text: "Malienas pagasts",
  },
  {
    key: "0021490",
    text: "Mālupes pagasts",
  },
  {
    key: "0021500",
    text: "Mārkalnes pagasts",
  },
  {
    key: "0021510",
    text: "Pededzes pagasts",
  },
  {
    key: "0021520",
    text: "Veclaicenes pagasts",
  },
  {
    key: "0021530",
    text: "Zeltiņu pagasts",
  },
  {
    key: "0021540",
    text: "Ziemera pagasts",
  },
  {
    key: "0026200",
    text: "Cēsis",
  },
  {
    key: "0026210",
    text: "Līgatne",
  },
  {
    key: "0026400",
    text: "Amatas pagasts",
  },
  {
    key: "0026410",
    text: "Drabešu pagasts",
  },
  {
    key: "0026420",
    text: "Dzērbenes pagasts",
  },
  {
    key: "0026430",
    text: "Inešu pagasts",
  },
  {
    key: "0026440",
    text: "Jaunpiebalgas pagasts",
  },
  {
    key: "0026450",
    text: "Kaives pagasts",
  },
  {
    key: "0026460",
    text: "Liepas pagasts",
  },
  {
    key: "0026470",
    text: "Līgatnes pagasts",
  },
  {
    key: "0026480",
    text: "Mārsnēnu pagasts",
  },
  {
    key: "0026490",
    text: "Nītaures pagasts",
  },
  {
    key: "0026500",
    text: "Priekuļu pagasts",
  },
  {
    key: "0026510",
    text: "Raiskuma pagasts",
  },
  {
    key: "0026520",
    text: "Skujenes pagasts",
  },
  {
    key: "0026530",
    text: "Stalbes pagasts",
  },
  {
    key: "0026540",
    text: "Straupes pagasts",
  },
  {
    key: "0026550",
    text: "Taurenes pagasts",
  },
  {
    key: "0026560",
    text: "Vaives pagasts",
  },
  {
    key: "0026570",
    text: "Vecpiebalgas pagasts",
  },
  {
    key: "0026580",
    text: "Veselavas pagasts",
  },
  {
    key: "0026590",
    text: "Zaubes pagasts",
  },
  {
    key: "0026600",
    text: "Zosēnu pagasts",
  },
  {
    key: "0029200",
    text: "Gulbene",
  },
  {
    key: "0029400",
    text: "Beļavas pagasts",
  },
  {
    key: "0029410",
    text: "Daukstu pagasts",
  },
  {
    key: "0029420",
    text: "Druvienas pagasts",
  },
  {
    key: "0029430",
    text: "Galgauskas pagasts",
  },
  {
    key: "0029440",
    text: "Jaungulbenes pagasts",
  },
  {
    key: "0029450",
    text: "Lejasciema pagasts",
  },
  {
    key: "0029460",
    text: "Litenes pagasts",
  },
  {
    key: "0029470",
    text: "Lizuma pagasts",
  },
  {
    key: "0029480",
    text: "Līgo pagasts",
  },
  {
    key: "0029490",
    text: "Rankas pagasts",
  },
  {
    key: "0029500",
    text: "Stāmerienas pagasts",
  },
  {
    key: "0029510",
    text: "Stradu pagasts",
  },
  {
    key: "0029520",
    text: "Tirzas pagasts",
  },
  {
    key: "0038200",
    text: "Cesvaine",
  },
  {
    key: "0038210",
    text: "Lubāna",
  },
  {
    key: "0038220",
    text: "Madona",
  },
  {
    key: "0038231",
    text: "Varakļāni",
  },
  {
    key: "0038400",
    text: "Aronas pagasts",
  },
  {
    key: "0038410",
    text: "Barkavas pagasts",
  },
  {
    key: "0038420",
    text: "Bērzaunes pagasts",
  },
  {
    key: "0038430",
    text: "Cesvaines pagasts",
  },
  {
    key: "0038440",
    text: "Dzelzavas pagasts",
  },
  {
    key: "0038450",
    text: "Ērgļu pagasts",
  },
  {
    key: "0038460",
    text: "Indrānu pagasts",
  },
  {
    key: "0038470",
    text: "Jumurdas pagasts",
  },
  {
    key: "0038480",
    text: "Kalsnavas pagasts",
  },
  {
    key: "0038490",
    text: "Lazdonas pagasts",
  },
  {
    key: "0038500",
    text: "Liezēres pagasts",
  },
  {
    key: "0038510",
    text: "Ļaudonas pagasts",
  },
  {
    key: "0038520",
    text: "Mārcienas pagasts",
  },
  {
    key: "0038530",
    text: "Mētrienas pagasts",
  },
  {
    key: "0038540",
    text: "Ošupes pagasts",
  },
  {
    key: "0038550",
    text: "Praulienas pagasts",
  },
  {
    key: "0038560",
    text: "Sarkaņu pagasts",
  },
  {
    key: "0038570",
    text: "Sausnējas pagasts",
  },
  {
    key: "0038580",
    text: "Vestienas pagasts",
  },
  {
    key: "0038591",
    text: "Murmastienes pagasts",
  },
  {
    key: "0038601",
    text: "Varakļānu pagasts",
  },
  {
    key: "0049200",
    text: "Ape",
  },
  {
    key: "0049210",
    text: "Smiltene",
  },
  {
    key: "0049400",
    text: "Apes pagasts",
  },
  {
    key: "0049410",
    text: "Bilskas pagasts",
  },
  {
    key: "0049420",
    text: "Blomes pagasts",
  },
  {
    key: "0049430",
    text: "Brantu pagasts",
  },
  {
    key: "0049440",
    text: "Drustu pagasts",
  },
  {
    key: "0049450",
    text: "Gaujienas pagasts",
  },
  {
    key: "0049460",
    text: "Grundzāles pagasts",
  },
  {
    key: "0049470",
    text: "Launkalnes pagasts",
  },
  {
    key: "0049480",
    text: "Palsmanes pagasts",
  },
  {
    key: "0049490",
    text: "Raunas pagasts",
  },
  {
    key: "0049500",
    text: "Smiltenes pagasts",
  },
  {
    key: "0049510",
    text: "Trapenes pagasts",
  },
  {
    key: "0049520",
    text: "Variņu pagasts",
  },
  {
    key: "0049530",
    text: "Virešu pagasts",
  },
  {
    key: "0053200",
    text: "Valka",
  },
  {
    key: "0053400",
    text: "Ērģemes pagasts",
  },
  {
    key: "0053410",
    text: "Kārķu pagasts",
  },
  {
    key: "0053420",
    text: "Valkas pagasts",
  },
  {
    key: "0053430",
    text: "Vijciema pagasts",
  },
  {
    key: "0053440",
    text: "Zvārtavas pagasts",
  },
  {
    key: "0054010",
    text: "Valmiera",
  },
  {
    key: "0054200",
    text: "Mazsalaca",
  },
  {
    key: "0054210",
    text: "Rūjiena",
  },
  {
    key: "0054220",
    text: "Seda",
  },
  {
    key: "0054230",
    text: "Strenči",
  },
  {
    key: "0054400",
    text: "Bērzaines pagasts",
  },
  {
    key: "0054410",
    text: "Brenguļu pagasts",
  },
  {
    key: "0054420",
    text: "Burtnieku pagasts",
  },
  {
    key: "0054430",
    text: "Dikļu pagasts",
  },
  {
    key: "0054440",
    text: "Ēveles pagasts",
  },
  {
    key: "0054450",
    text: "Ipiķu pagasts",
  },
  {
    key: "0054460",
    text: "Jeru pagasts",
  },
  {
    key: "0054470",
    text: "Jērcēnu pagasts",
  },
  {
    key: "0054480",
    text: "Kauguru pagasts",
  },
  {
    key: "0054490",
    text: "Kocēnu pagasts",
  },
  {
    key: "0054500",
    text: "Ķoņu pagasts",
  },
  {
    key: "0054510",
    text: "Lodes pagasts",
  },
  {
    key: "0054520",
    text: "Matīšu pagasts",
  },
  {
    key: "0054530",
    text: "Mazsalacas pagasts",
  },
  {
    key: "0054540",
    text: "Naukšēnu pagasts",
  },
  {
    key: "0054550",
    text: "Plāņu pagasts",
  },
  {
    key: "0054560",
    text: "Ramatas pagasts",
  },
  {
    key: "0054570",
    text: "Rencēnu pagasts",
  },
  {
    key: "0054580",
    text: "Sēļu pagasts",
  },
  {
    key: "0054590",
    text: "Skaņkalnes pagasts",
  },
  {
    key: "0054600",
    text: "Trikātas pagasts",
  },
  {
    key: "0054610",
    text: "Vaidavas pagasts",
  },
  {
    key: "0054620",
    text: "Valmieras pagasts",
  },
  {
    key: "0054630",
    text: "Vecates pagasts",
  },
  {
    key: "0054640",
    text: "Vilpulkas pagasts",
  },
  {
    key: "0054650",
    text: "Zilākalna pagasts",
  },
  {
    key: "0020200",
    text: "Aizkraukle",
  },
  {
    key: "0020210",
    text: "Jaunjelgava",
  },
  {
    key: "0020220",
    text: "Koknese",
  },
  {
    key: "0020230",
    text: "Pļaviņas",
  },
  {
    key: "0020400",
    text: "Aiviekstes pagasts",
  },
  {
    key: "0020410",
    text: "Aizkraukles pagasts",
  },
  {
    key: "0020420",
    text: "Bebru pagasts",
  },
  {
    key: "0020430",
    text: "Daudzeses pagasts",
  },
  {
    key: "0020440",
    text: "Iršu pagasts",
  },
  {
    key: "0020450",
    text: "Jaunjelgavas pagasts",
  },
  {
    key: "0020460",
    text: "Klintaines pagasts",
  },
  {
    key: "0020470",
    text: "Kokneses pagasts",
  },
  {
    key: "0020480",
    text: "Mazzalves pagasts",
  },
  {
    key: "0020490",
    text: "Neretas pagasts",
  },
  {
    key: "0020500",
    text: "Pilskalnes pagasts",
  },
  {
    key: "0020510",
    text: "Seces pagasts",
  },
  {
    key: "0020520",
    text: "Sērenes pagasts",
  },
  {
    key: "0020530",
    text: "Skrīveru pagasts",
  },
  {
    key: "0020540",
    text: "Staburaga pagasts",
  },
  {
    key: "0020550",
    text: "Sunākstes pagasts",
  },
  {
    key: "0020560",
    text: "Vietalvas pagasts",
  },
  {
    key: "0020570",
    text: "Zalves pagasts",
  },
  {
    key: "0025200",
    text: "Bauska",
  },
  {
    key: "0025210",
    text: "Iecava",
  },
  {
    key: "0025400",
    text: "Bārbeles pagasts",
  },
  {
    key: "0025410",
    text: "Brunavas pagasts",
  },
  {
    key: "0025420",
    text: "Ceraukstes pagasts",
  },
  {
    key: "0025430",
    text: "Codes pagasts",
  },
  {
    key: "0025440",
    text: "Dāviņu pagasts",
  },
  {
    key: "0025450",
    text: "Gailīšu pagasts",
  },
  {
    key: "0025460",
    text: "Iecavas pagasts",
  },
  {
    key: "0025470",
    text: "Īslīces pagasts",
  },
  {
    key: "0025480",
    text: "Kurmenes pagasts",
  },
  {
    key: "0025490",
    text: "Mežotnes pagasts",
  },
  {
    key: "0025500",
    text: "Rundāles pagasts",
  },
  {
    key: "0025510",
    text: "Skaistkalnes pagasts",
  },
  {
    key: "0025520",
    text: "Stelpes pagasts",
  },
  {
    key: "0025530",
    text: "Svitenes pagasts",
  },
  {
    key: "0025540",
    text: "Valles pagasts",
  },
  {
    key: "0025550",
    text: "Vecsaules pagasts",
  },
  {
    key: "0025560",
    text: "Vecumnieku pagasts",
  },
  {
    key: "0025570",
    text: "Viesturu pagasts",
  },
  {
    key: "0028200",
    text: "Auce",
  },
  {
    key: "0028210",
    text: "Dobele",
  },
  {
    key: "0028400",
    text: "Annenieku pagasts",
  },
  {
    key: "0028410",
    text: "Augstkalnes pagasts",
  },
  {
    key: "0028420",
    text: "Auru pagasts",
  },
  {
    key: "0028430",
    text: "Bēnes pagasts",
  },
  {
    key: "0028440",
    text: "Bērzes pagasts",
  },
  {
    key: "0028450",
    text: "Bikstu pagasts",
  },
  {
    key: "0028460",
    text: "Bukaišu pagasts",
  },
  {
    key: "0028470",
    text: "Dobeles pagasts",
  },
  {
    key: "0028480",
    text: "Īles pagasts",
  },
  {
    key: "0028490",
    text: "Jaunbērzes pagasts",
  },
  {
    key: "0028500",
    text: "Krimūnu pagasts",
  },
  {
    key: "0028510",
    text: "Lielauces pagasts",
  },
  {
    key: "0028520",
    text: "Naudītes pagasts",
  },
  {
    key: "0028530",
    text: "Penkules pagasts",
  },
  {
    key: "0028540",
    text: "Tērvetes pagasts",
  },
  {
    key: "0028550",
    text: "Ukru pagasts",
  },
  {
    key: "0028560",
    text: "Vecauces pagasts",
  },
  {
    key: "0028570",
    text: "Vītiņu pagasts",
  },
  {
    key: "0028580",
    text: "Zebrenes pagasts",
  },
  {
    key: "0030400",
    text: "Cenu pagasts",
  },
  {
    key: "0030410",
    text: "Elejas pagasts",
  },
  {
    key: "0030420",
    text: "Glūdas pagasts",
  },
  {
    key: "0030430",
    text: "Jaunsvirlaukas pagasts",
  },
  {
    key: "0030440",
    text: "Kalnciema pagasts",
  },
  {
    key: "0030450",
    text: "Lielplatones pagasts",
  },
  {
    key: "0030460",
    text: "Līvbērzes pagasts",
  },
  {
    key: "0030470",
    text: "Ozolnieku pagasts",
  },
  {
    key: "0030480",
    text: "Platones pagasts",
  },
  {
    key: "0030490",
    text: "Salgales pagasts",
  },
  {
    key: "0030500",
    text: "Sesavas pagasts",
  },
  {
    key: "0030510",
    text: "Svētes pagasts",
  },
  {
    key: "0030520",
    text: "Valgundes pagasts",
  },
  {
    key: "0030530",
    text: "Vilces pagasts",
  },
  {
    key: "0030540",
    text: "Vircavas pagasts",
  },
  {
    key: "0030550",
    text: "Zaļenieku pagasts",
  },
  {
    key: "0031010",
    text: "Jēkabpils",
  },
  {
    key: "0031200",
    text: "Aknīste",
  },
  {
    key: "0031210",
    text: "Viesīte",
  },
  {
    key: "0031400",
    text: "Aknīstes pagasts",
  },
  {
    key: "0031410",
    text: "Asares pagasts",
  },
  {
    key: "0031420",
    text: "Atašienes pagasts",
  },
  {
    key: "0031430",
    text: "Ābeļu pagasts",
  },
  {
    key: "0031440",
    text: "Dignājas pagasts",
  },
  {
    key: "0031450",
    text: "Dunavas pagasts",
  },
  {
    key: "0031460",
    text: "Elkšņu pagasts",
  },
  {
    key: "0031470",
    text: "Gārsenes pagasts",
  },
  {
    key: "0031480",
    text: "Kalna pagasts",
  },
  {
    key: "0031490",
    text: "Krustpils pagasts",
  },
  {
    key: "0031500",
    text: "Kūku pagasts",
  },
  {
    key: "0031510",
    text: "Leimaņu pagasts",
  },
  {
    key: "0031520",
    text: "Mežāres pagasts",
  },
  {
    key: "0031530",
    text: "Rites pagasts",
  },
  {
    key: "0031540",
    text: "Rubenes pagasts",
  },
  {
    key: "0031550",
    text: "Salas pagasts",
  },
  {
    key: "0031560",
    text: "Saukas pagasts",
  },
  {
    key: "0031570",
    text: "Sēlpils pagasts",
  },
  {
    key: "0031580",
    text: "Variešu pagasts",
  },
  {
    key: "0031590",
    text: "Viesītes pagasts",
  },
  {
    key: "0031600",
    text: "Vīpes pagasts",
  },
  {
    key: "0031610",
    text: "Zasas pagasts",
  },
];

// City options
export const getCityOptions = (t: TFunction): OptionItem[] => [
  { key: "IQ", text: "IQ" },
  { key: "LV", text: "LV" },
  { key: "other", text: t("qtg.proposal.clientData.options.other") },
];

// Bonus malus options
export const getBonusMalusOptions = (t: TFunction): OptionItem[] => [
  { key: "1", text: "1" },
  { key: "2", text: "2" },
  { key: "3", text: "3" },
  { key: "4", text: "4" },
  { key: "5", text: "5" },
];

// Social discount options
export const getSocialDiscountOptions = (t: TFunction): OptionItem[] => [
  { key: "none", text: t("qtg.proposal.clientData.options.none") },
  { key: "disabled", text: t("qtg.proposal.clientData.options.disabled") },
  { key: "veteran", text: t("qtg.proposal.clientData.options.veteran") },
  {
    key: "large_family",
    text: t("qtg.proposal.clientData.options.largeFamily"),
  },
];

// Territory of validity options (Coverage Territory)
// This function now maps taxonomy codes to translated labels
export const getTerritoryOfValidityOptions = (t: TFunction): OptionItem[] => [
  {
    key: "CoverageTerritory.LATVIA",
    text: t("CoverageTerritory.LATVIA", "Latvia"),
  },
  {
    key: "CoverageTerritory.EUROPE_WITH_CIS",
    text: t("CoverageTerritory.EUROPE_WITH_CIS", "Europe with CIS"),
  },
  {
    key: "CoverageTerritory.EUROPE_WITHOUT_CIS",
    text: t("CoverageTerritory.EUROPE_WITHOUT_CIS", "Europe without CIS"),
  },
  {
    key: "CoverageTerritory.BALTICS",
    text: t("CoverageTerritory.BALTICS", "Baltics"),
  },
];

// Risk assessment options
export const getRiskAssessmentOptions = (t: TFunction): OptionItem[] => [
  { key: "low", text: t("qtg.proposal.additionalData.options.low") },
  { key: "medium", text: t("qtg.proposal.additionalData.options.medium") },
  { key: "high", text: t("qtg.proposal.additionalData.options.high") },
  { key: "very_high", text: t("qtg.proposal.additionalData.options.veryHigh") },
];

// Payment method options
export const getPaymentMethodOptions = (t: TFunction): OptionItem[] => [
  {
    key: "bank_transfer",
    text: t("qtg.proposal.additionalData.options.bankTransfer"),
  },
  {
    key: "credit_card",
    text: t("qtg.proposal.additionalData.options.creditCard"),
  },
  {
    key: "direct_debit",
    text: t("qtg.proposal.additionalData.options.directDebit"),
  },
  { key: "cash", text: t("qtg.proposal.additionalData.options.cash") },
];

// Billing frequency options
export const getBillingFrequencyOptions = (t: TFunction): OptionItem[] => [
  { key: "monthly", text: t("qtg.proposal.additionalData.options.monthly") },
  {
    key: "quarterly",
    text: t("qtg.proposal.additionalData.options.quarterly"),
  },
  {
    key: "semi_annual",
    text: t("qtg.proposal.additionalData.options.semiAnnual"),
  },
  { key: "annual", text: t("qtg.proposal.additionalData.options.annual") },
];

// Credit rating options
export const getCreditRatingOptions = (t: TFunction): OptionItem[] => [
  {
    key: "excellent",
    text: t("qtg.proposal.additionalData.options.excellent"),
  },
  { key: "good", text: t("qtg.proposal.additionalData.options.good") },
  { key: "fair", text: t("qtg.proposal.additionalData.options.fair") },
  { key: "poor", text: t("qtg.proposal.additionalData.options.poor") },
  { key: "unknown", text: t("qtg.proposal.additionalData.options.unknown") },
];

// Marketing source options
export const getMarketingSourceOptions = (t: TFunction): OptionItem[] => [
  { key: "website", text: t("qtg.proposal.additionalData.options.website") },
  { key: "referral", text: t("qtg.proposal.additionalData.options.referral") },
  {
    key: "advertisement",
    text: t("qtg.proposal.additionalData.options.advertisement"),
  },
  { key: "broker", text: t("qtg.proposal.additionalData.options.broker") },
  { key: "phone", text: t("qtg.proposal.additionalData.options.phone") },
  { key: "other", text: t("qtg.proposal.additionalData.options.other") },
];

// Policy delivery type options
export const getPolicyDeliveryTypeOptions = (t: TFunction): OptionItem[] => [
  { key: "-", text: "-" },
  { key: "email", text: t("qtg.proposal.additionalData.options.email") },
  { key: "post", text: t("qtg.proposal.additionalData.options.post") },
  { key: "pickup", text: t("qtg.proposal.additionalData.options.pickup") },
];

// Broker options (these would typically come from an API)
export const getBrokerOptions = (t: TFunction): OptionItem[] => [
  { key: "-", text: "-" },
  { key: "bojanic_sasa", text: "Bojanic Sasa" },
  {
    key: "other_broker",
    text: t("qtg.proposal.additionalData.options.otherBroker"),
  },
];

// Account manager options (these would typically come from an API)
export const getAccountManagerOptions = (t: TFunction): OptionItem[] => [
  { key: "-", text: "-" },
  { key: "manager1", text: t("qtg.proposal.additionalData.options.manager1") },
  { key: "manager2", text: t("qtg.proposal.additionalData.options.manager2") },
];

// Sales person options (these would typically come from an API)
export const getSalesPersonOptions = (t: TFunction): OptionItem[] => [
  { key: "-", text: "-" },
  { key: "sales1", text: t("qtg.proposal.additionalData.options.sales1") },
  { key: "sales2", text: t("qtg.proposal.additionalData.options.sales2") },
];

// Responsible person options (these would typically come from an API)
export const getResponsiblePersonOptions = (t: TFunction): OptionItem[] => [
  { key: "-", text: "-" },
  { key: "bojanic_sasa", text: "Bojanic Sasa" },
  {
    key: "other_person",
    text: t("qtg.proposal.additionalData.options.otherPerson"),
  },
];

// Keep the old static exports for backward compatibility
export const clientTypeOptions: OptionItem[] = [
  { key: "individual", text: "Individual" },
  { key: "legal_entity", text: "Legal Entity" },
];

export const genderOptions: OptionItem[] = [
  { key: "male", text: "Male" },
  { key: "female", text: "Female" },
  { key: "other", text: "Other" },
];

export const documentTypeOptions: OptionItem[] = [
  { key: "id_card", text: "ID Card" },
  { key: "passport", text: "Passport" },
  { key: "driver_license", text: "Driver's License" },
  { key: "residence_permit", text: "Residence Permit" },
];

export const countryOptions: OptionItem[] = [
  { key: "LV", text: "Latvia" },
  { key: "EE", text: "Estonia" },
  { key: "LT", text: "Lithuania" },
  { key: "DE", text: "Germany" },
  { key: "US", text: "United States" },
  { key: "GB", text: "United Kingdom" },
  { key: "FR", text: "France" },
  { key: "IT", text: "Italy" },
  { key: "ES", text: "Spain" },
  { key: "PL", text: "Poland" },
  { key: "SE", text: "Sweden" },
  { key: "DK", text: "Denmark" },
  { key: "NO", text: "Norway" },
  { key: "FI", text: "Finland" },
];
